import React, { useEffect } from 'react';
import { NavLink, Outlet, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingCart, FolderTree, Users, Ticket, LogOut, ExternalLink, Layers, Boxes, Star, HelpCircle, Settings, MessageSquareText, Home as HomeIcon, Gift, UserCog, History, Newspaper, FileText } from 'lucide-react';
import { useStore } from '@/context/StoreContext';
import { cx } from '@/lib/format';
import { isAdminRole, permsOf } from '@/lib/permissions';
import api from '@/lib/api';

// Grouped navigation — each item: [path, label, Icon, end, resource]
const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [['/admin', 'Dashboard', LayoutDashboard, true, 'analytics']],
  },
  {
    label: 'Catalog',
    items: [
      ['/admin/products', 'Products', Package, false, 'products'],
      ['/admin/bundles', 'Bundles', Layers, false, 'products'],
      ['/admin/categories', 'Categories', FolderTree, false, 'categories'],
      ['/admin/inventory', 'Inventory', Boxes, false, 'inventory'],
    ],
  },
  {
    label: 'Sales',
    items: [
      ['/admin/orders', 'Orders', ShoppingCart, false, 'orders'],
      ['/admin/customers', 'Customers', Users, false, 'customers'],
    ],
  },
  {
    label: 'Marketing',
    items: [
      ['/admin/coupons', 'Coupons', Ticket, false, 'coupons'],
      ['/admin/gift-finder', 'Gift Finder', Gift, false, 'gift_finder'],
    ],
  },
  {
    label: 'Content',
    items: [
      ['/admin/home', 'Home Page', HomeIcon, false, 'home_cms'],
      ['/admin/blog', 'Blog', Newspaper, false, 'home_cms'],
      ['/admin/pages', 'Pages', FileText, false, 'home_cms'],
    ],
  },
  {
    label: 'Moderation',
    items: [
      ['/admin/inquiries', 'Inquiries', MessageSquareText, false, 'customers'],
      ['/admin/reviews', 'Reviews', Star, false, 'products'],
      ['/admin/questions', 'Q&A', HelpCircle, false, 'products'],
    ],
  },
  {
    label: 'System',
    items: [
      ['/admin/users', 'Admin Users', UserCog, false, 'admin_users'],
      ['/admin/activity', 'Activity Log', History, false, 'admin_users'],
      ['/admin/settings', 'Settings', Settings, false, 'settings'],
    ],
  },
];

const ROUTE_RESOURCE = {
  '': 'analytics', products: 'products', bundles: 'products', categories: 'categories',
  inventory: 'inventory', orders: 'orders', customers: 'customers', coupons: 'coupons',
  reviews: 'products', questions: 'products', inquiries: 'customers', home: 'home_cms', blog: 'home_cms', pages: 'home_cms',
  'gift-finder': 'gift_finder', settings: 'settings', users: 'admin_users', activity: 'admin_users',
};

export default function AdminLayout() {
  const { user, isAuthed, logout, setUser } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const authed = isAuthed && isAdminRole(user);

  useEffect(() => {
    if (!authed) return;
    api.get('/auth/me').then((r) => {
      setUser(r.data);
      localStorage.setItem('gaze_user', JSON.stringify(r.data));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) return <Navigate to="/admin/login" replace />;

  const perms = permsOf(user);
  const visibleGroups = NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter(([, , , , res]) => !res || perms.includes(res)) })).filter((g) => g.items.length > 0);
  const flatNav = visibleGroups.flatMap((g) => g.items);

  const segment = location.pathname.replace(/^\/admin\/?/, '').split('/')[0];
  const resource = ROUTE_RESOURCE[segment];
  if (resource && !perms.includes(resource)) {
    const fallback = flatNav[0]?.[0];
    if (fallback) return <Navigate to={fallback} replace />;
  }

  return (
    <div className="flex min-h-screen bg-[hsl(var(--gaze-ivory))]">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col bg-[hsl(var(--primary))] text-white md:flex" data-testid="admin-sidebar">
        <div className="px-6 py-5"><span className="font-display text-2xl">Gaze</span><span className="ml-1 text-xs text-[hsl(var(--gaze-gold))]">Admin</span></div>
        <nav className="flex-1 space-y-4 overflow-y-auto px-3 pb-6">
          {visibleGroups.map((group) => (
            <div key={group.label}>
              <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-white/40">{group.label}</p>
              <div className="space-y-1">
                {group.items.map(([to, label, Icon, end]) => (
                  <NavLink key={to} to={to} end={end} className={({ isActive }) => cx('flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium', isActive ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10')}>
                    <Icon size={16} /> {label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="space-y-1 border-t border-white/10 p-3">
          <a href="/" className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-white/70 hover:bg-white/10"><ExternalLink size={16} /> View Store</a>
          <button onClick={() => { logout(); navigate('/admin/login'); }} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm text-white/70 hover:bg-white/10"><LogOut size={16} /> Logout</button>
        </div>
      </aside>
      <div className="flex-1">
        <div className="flex items-center gap-3 overflow-x-auto border-b border-border bg-[hsl(var(--primary))] px-3 py-2 text-white md:hidden">
          {flatNav.map(([to, label, Icon, end]) => <NavLink key={to} to={to} end={end} className={({ isActive }) => cx('whitespace-nowrap rounded-full px-3 py-1.5 text-xs', isActive ? 'bg-white/20' : 'text-white/70')}>{label}</NavLink>)}
        </div>
        <main className="p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
